import '../index.scss';
import { ShortAnswerIcon } from '../../../../../../components/Icons/ShortAnswerIcon';
import { Paragraph } from '../../../../../../components/Icons/Paragraph';
import { SingleChoice } from '../../../../../../components/Icons/SingleChoice';
import { MultipleChoice } from '../../../../../../components/Icons/MultipleChoice';
import { DropdownIcon } from '../../../../../../components/Icons/DropdownIcon';
import { ExplanationBanner } from '../../../../../../components/Icons/ExplanationBanner';
import { Button, Checkbox, Divider, Form, message, Modal, Switch, Tooltip } from 'antd';
import { FloatInput } from '../../../../../../components/Fields/FloatInput';
import { DndContext, pointerWithin, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import type { DragCancelEvent, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DraggableItem } from './components/DraggableItem';
import { SortableFormItem, SortableFormItemDragOverlay } from './components/SortableFormItem';
import { DroppableArea } from './components/DroppableArea';
import { SupplierQuestionaryFormIcon } from '../../../../../../components/Icons/SupplierQuestionaryFormIcon';
import { AddSectionIcon } from '../../../../../../components/Icons/AddSectionIcon';
import BGImage from '../../../../../../assets/BG.png';
import Plus from '../../../../../../assets/Plus, Add.svg';
import {
    useLazyGetBuilderComponentsQuery,
    useLazyGetLinkedComponentsQuery,
    useLazyShowBuilderDataQuery
} from '../../../../../../api/expenseAreas';
import { FieldI, FormItem, LinkedComponentI, Section, SettingI } from '../../../../../../api/types/expenseareas';
import { FloatTextArea } from '../../../../../../components/Fields/FloatTextArea';
import Delete from '../../../../../../components/Icons/Delete';
import { CustomMultiSelect } from '../../../../../../components/Fields/CustomMultiSelect';
import { GoToSectionIcon } from '../../../../../../components/Icons/GoToSectionIcon';
import { FloatSelect } from '../../../../../../components/Fields/FloatSelect';
import { Loader } from '../../../../../../components/Loader/Loader';
import ArrowDown from '../../../../../../components/Icons/ArrowDown';
import ArrowDownDown from '../../../../../../components/Icons/ArrowDownDown';
import i from '../../../../../../assets/i.svg';
import { LinkedFieldsComponent } from './components/LinkedFieldsComponent';
import { ClipIcon } from '../../../../../../components/Icons/ClipIcon';
import useKikOffQuestionaryController from './KikOffQuestionary.controller';
import {
    computeKickoffResponseValidation,
    isKickoffFieldSatisfiedClientPublishRules
} from './kickoffFieldClientValidation';
import { ResponceValidation } from './components/ResponceValidation';
import { getFirstBuilderValidationErrorElement, scrollIntoFormColumnSmooth } from '../builderFormUtils';
import { useAdminFormPreview } from '../../useAdminFormPreview';

const hasKickOffFieldLabel = (label: unknown): boolean => typeof label === 'string' && label.trim().length > 0;

const iconsMap: Record<string, (fill?: string) => JSX.Element> = {
    'short-answer': (fill) => <ShortAnswerIcon fill={fill} />,
    paragraph: (fill) => <Paragraph fill={fill} />,
    'single-select': (fill) => <SingleChoice fill={fill} />,
    'multiple-choice': (fill) => <MultipleChoice fill={fill} />,
    dropdown: (fill) => <DropdownIcon fill={fill} />,
    'file-upload': (fill) => <ExplanationBanner fill={fill} />,
    'explanation-banner': (fill) => <ExplanationBanner fill={fill} />
};

export const KikOffQuestionaryBuilder = () => {
    const {
        publish,
        saveAsDraftFunc,
        isSuccessSaveAsDraft,
        isLoadingDraft,
        data,
        setData,
        isCantPublishModal,
        setIsCantPublishModal,
        sectionsError,
        setSectionsError,
        fieldsError,
        setFieldsError,
        isSuccessPublishForm,
        ExpenseAreaId,
        isLoadingPublished,
        addNewSection,
        selectedElement,
        setSelectedElement,
        updateFieldOrSection,
        updateSettingField,
        updateOptionField,
        addOption,
        updateRadioOptionSelectedInSetting,
        updateOptionSelectedInSetting,
        selectAllOptions,
        deselectAllOptions,
        removeOption,
        removeItem,
        removeSection,
        findItem,
        maxSwitchValue,
        moveSectionToStart,
        moveSectionToEnd,
        moveSectionDown,
        moveSectionUp,
        updateNestedOptionSelected,
        deselectAllNestedOptions,
        updateNestedOptionField,
        updateOptionMultiSelectedInSetting,
        setComponents
    } = useKikOffQuestionaryController();

    const { openPreview, isOpeningPreview, previewModal } = useAdminFormPreview(
        'kick-off-questionnaire',
        ExpenseAreaId
    );

    const [fields, setFields] = useState<any>([]);
    const [linkedFields, setLinkedFields] = useState<any>([]);

    const [isDeleteOption, setIsDeleteOption] = useState<null | number>(null);
    const [isDeleteItem, setIsDeleteItem] = useState<boolean>(false);
    const [isDeleteSection, setIsDeleteSection] = useState<boolean>(false);

    const [activeItem, setActiveItem] = useState<any | null>(null);
    const [selectedTab, setSelectedTab] = useState<'basic' | 'linked'>('basic');

    const formScrollRef = useRef<HTMLDivElement>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 }
        })
    );

    const visibleSections = useMemo(() => data?.sections ?? [], [data?.sections]);

    const [getBuilderComponent, { data: componentsData }] = useLazyGetBuilderComponentsQuery();
    const [getLinkedComponent, { data: linkedComponentsData }] = useLazyGetLinkedComponentsQuery();

    const [showBuilderData, { data: FormData }] = useLazyShowBuilderDataQuery();

    useEffect(() => {
        if (!componentsData) return;
        setComponents(componentsData);
    }, [componentsData]);

    useEffect(() => {
        let sectionErrors: string[] = [...sectionsError];
        let sectionFieldsErrors: string[] = [...fieldsError];
        data?.sections?.forEach((section) => {
            if (section.title && section.fields?.length) {
                sectionErrors = sectionErrors.filter((id) => String(id) !== String(section?.localId));
            }

            section?.fields.forEach((field) => {
                const type = field.type;

                if (
                    type === 'short-answer' ||
                    type === 'paragraph' ||
                    type === 'explanation-banner' ||
                    type === 'file-upload'
                ) {
                    const { isValidResponseValidation } = computeKickoffResponseValidation(field);
                    if (hasKickOffFieldLabel(field.label) && isValidResponseValidation) {
                        sectionFieldsErrors = sectionFieldsErrors.filter((id) => String(id) !== String(field?.id));
                    }
                } else if (type === 'single-select' || type === 'multiple-choice' || type === 'dropdown') {
                    if (isKickoffFieldSatisfiedClientPublishRules(field)) {
                        sectionFieldsErrors = sectionFieldsErrors.filter((id) => String(id) !== String(field?.id));
                    }
                }
            });
        });
        setSectionsError(sectionErrors);
        setFieldsError(sectionFieldsErrors);
    }, [data]);

    const showFormData = () => {
        if (ExpenseAreaId) {
            const baseData = {
                id: ExpenseAreaId,
                type: 'kick-off-questionnaire'
            };
            getBuilderComponent(baseData);
            getLinkedComponent(baseData);
            showBuilderData(baseData);
        }
    };

    const queueScrollToFirstValidationError = (sectionErrors: string[], fieldsErrors: string[]) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const container = formScrollRef.current;
                if (!container) return;
                const el = getFirstBuilderValidationErrorElement(
                    container,
                    visibleSections,
                    sectionErrors,
                    fieldsErrors
                );
                scrollIntoFormColumnSmooth(container, el);
            });
        });
    };

    const handlePublishClick = () => {
        const result = publish();
        if (!result.ok && result.reason === 'invalid') {
            queueScrollToFirstValidationError(result.sectionErrors, result.fieldsErrors);
        }
    };

    useEffect(() => {
        if (isSuccessSaveAsDraft) {
            message?.success('Your form has been saved as a draft');
            showFormData();
        }
    }, [isSuccessSaveAsDraft]);

    useEffect(() => {
        if (isSuccessPublishForm) {
            message?.success('Your form has been published');
            showFormData();
        }
    }, [isSuccessPublishForm]);

    useEffect(() => {
        if (FormData) {
            const sectionLocalId = FormData?.data?.sections?.map((section) => ({
                ...section,
                localId: section.id
            }));
            setData(
                structuredClone({
                    ...FormData?.data,
                    sections: sectionLocalId
                })
            );
        }
    }, [FormData, isSuccessPublishForm, isSuccessSaveAsDraft]);

    const selectedElementData = useMemo(() => {
        if (!selectedElement) return null;
        return (
            data?.sections?.flatMap((elem) => elem?.fields)?.find((item) => item?.id + '' === selectedElement) || null
        );
    }, [selectedElement, data]);

    useEffect(() => {
        if (componentsData && linkedComponentsData && selectedTab) {
            if (selectedTab === 'linked') {
                const linkedIdsFromSections =
                    data?.sections?.flatMap((section) => section.fields?.map((f: FormItem) => f.link_id)) || [];

                const fields = linkedComponentsData?.data?.map((elem: LinkedComponentI) => ({
                    ...elem,
                    fields: elem.fields
                        .filter((field) => !linkedIdsFromSections.includes(field.link_id))
                        .map((field) => ({
                            ...field,
                            id: `${field.type}-${field?.link_id}`
                        }))
                }));

                setLinkedFields(fields);
            } else {
                const fields = componentsData?.data?.fields.map((elem: FormItem) => ({
                    ...elem,
                    id: elem.type
                }));
                setFields(fields);
            }
        }
    }, [componentsData, linkedComponentsData, selectedTab, data]);

    useEffect(() => {
        showFormData();
    }, [ExpenseAreaId]);

    const handleDragCancel = (_event: DragCancelEvent) => {
        document.body.classList.remove('dragging');
        setActiveItem(null);
    };

    const handleDragStart = (event: DragStartEvent) => {
        document.body.classList.add('dragging');
        const { active } = event;
        const id = active.id.toString();

        if (selectedTab === 'basic') {
            setSelectedElement(id);
            const fromPalette = fields.find((el: any) => el.type === id);
            if (fromPalette) {
                setActiveItem({ ...fromPalette, origin: 'palette' });
                return;
            }
            if (!data?.sections) return;
            for (const section of data?.sections) {
                const found = section.fields.find((item) => item.id === id);
                if (found) {
                    setActiveItem({ ...found, origin: 'form' });
                    return;
                }
            }
        } else {
            setSelectedElement(id);
            const fromPalette = linkedFields
                ?.flatMap((elem: LinkedComponentI) => elem.fields)
                .find((el: any) => el.id === id);
            if (fromPalette) {
                setActiveItem({ ...fromPalette, origin: 'palette' });
                return;
            }
            if (!data?.sections) return;
            for (const section of data?.sections) {
                //@ts-ignore
                const found = section.fields.find((item: FieldI) => item.id === id);

                if (found) {
                    setActiveItem({ ...found, origin: 'form' });
                    return;
                }
            }
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        document.body.classList.remove('dragging');
        const { active, over } = event;

        try {
            if (!over) return;

            if (selectedTab === 'basic') {
                const activeId = active.id;

                const fromPalette = fields.find((el: any) => el.id === activeId);

                let sourceSectionIndex = -1;
                let destinationSectionIndex = -1;
                let activeItem: FormItem | null = null;

                data?.sections?.forEach((section, i) => {
                    if (section?.fields?.find((item) => item.id === activeId)) {
                        sourceSectionIndex = i;
                        activeItem = section?.fields?.find((item) => item?.id === activeId)!;
                    }
                    if (section.id === over?.id || section.fields.find((item) => item.id === over?.id)) {
                        destinationSectionIndex = i;
                    }
                });

                if (fromPalette && destinationSectionIndex !== -1) {
                    const base = fromPalette;
                    const localId = `${base.id}-${Date.now()}`;
                    setSelectedElement(localId);

                    const newItem: FormItem = {
                        ...base,
                        id: localId
                    };

                    setData((prev) => {
                        if (!prev) return prev;

                        const updatedSections = [...prev.sections];
                        updatedSections[destinationSectionIndex]?.fields?.push(newItem);

                        return {
                            ...prev,
                            sections: updatedSections
                        };
                    });
                    return;
                }

                if (activeItem && sourceSectionIndex !== -1 && destinationSectionIndex !== -1) {
                    setData((prev) => {
                        if (!prev) return prev;

                        const updatedSections = [...prev.sections];
                        const sourceItems = [...updatedSections[sourceSectionIndex].fields];
                        const destItems = [...updatedSections[destinationSectionIndex].fields];

                        if (sourceSectionIndex === destinationSectionIndex) {
                            const oldIndex = sourceItems.findIndex((item) => item.id === active.id);
                            const newIndex = sourceItems.findIndex((item) => item.id === over.id);
                            updatedSections[sourceSectionIndex].fields = arrayMove(sourceItems, oldIndex, newIndex);
                        } else {
                            const movingItemIndex = sourceItems.findIndex((item) => item.id === active.id);
                            if (movingItemIndex === -1) return prev;

                            const movingItem = sourceItems[movingItemIndex];

                            sourceItems.splice(movingItemIndex, 1);
                            updatedSections[sourceSectionIndex].fields = sourceItems;

                            const overIndex = destItems.findIndex((item) => item.id === over.id);
                            if (overIndex === -1) {
                                destItems.push(movingItem);
                            } else {
                                destItems.splice(overIndex, 0, movingItem);
                            }
                            updatedSections[destinationSectionIndex].fields = destItems;
                        }

                        return {
                            ...prev,
                            sections: updatedSections
                        };
                    });
                }
            } else {
                const fromPalette = linkedFields
                    ?.flatMap((elem: LinkedComponentI) => elem.fields)
                    .find((el: any) => el.id === active?.id);
                let sourceSectionIndex = -1;
                let destinationSectionIndex = -1;
                let activeItem: FormItem | null = null;
                data?.sections?.forEach((section: Section, i: number) => {
                    if (section?.fields?.find((item) => item.id === active?.id)) {
                        sourceSectionIndex = i;
                        activeItem = section?.fields?.find((item) => item?.id === active?.id)!;
                    }
                    if (section.id === over?.id || section.fields.find((item) => item.id === over?.id)) {
                        destinationSectionIndex = i;
                    }
                });
                if (fromPalette && destinationSectionIndex !== -1) {
                    const base = fromPalette;
                    const localId = `${base.id}-${Date.now()}`;
                    setSelectedElement(localId);
                    const newItem: FormItem = {
                        ...base,
                        id: localId
                    };
                    setData((prev) => {
                        if (!prev) return prev;
                        const updatedSections = [...prev.sections];
                        updatedSections[destinationSectionIndex]?.fields?.push(newItem);
                        return {
                            ...prev,
                            sections: updatedSections
                        };
                    });
                    return;
                }
                if (activeItem && sourceSectionIndex !== -1 && destinationSectionIndex !== -1) {
                    setData((prev) => {
                        if (!prev) return prev;
                        // Копируем массив секций
                        const updatedSections = [...prev.sections];
                        const sourceItems = [...updatedSections[sourceSectionIndex].fields];
                        const destItems = [...updatedSections[destinationSectionIndex].fields];
                        if (sourceSectionIndex === destinationSectionIndex) {
                            const oldIndex = sourceItems.findIndex((item) => item.id === active.id);
                            const newIndex = sourceItems.findIndex((item) => item.id === over.id);
                            updatedSections[sourceSectionIndex].fields = arrayMove(sourceItems, oldIndex, newIndex);
                        } else {
                            const movingItemIndex = sourceItems.findIndex((item) => item.id === active.id);
                            if (movingItemIndex === -1) return prev;
                            const movingItem = sourceItems[movingItemIndex];
                            sourceItems.splice(movingItemIndex, 1);
                            updatedSections[sourceSectionIndex].fields = sourceItems;
                            const overIndex = destItems.findIndex((item) => item.id === over.id);
                            if (overIndex === -1) {
                                destItems.push(movingItem);
                            } else {
                                destItems.splice(overIndex, 0, movingItem);
                            }
                            updatedSections[destinationSectionIndex].fields = destItems;
                        }
                        return {
                            ...prev,
                            sections: updatedSections
                        };
                    });
                }
            }
        } finally {
            setActiveItem(null);
        }
    };

    const fieldSettings = findItem();
    const fieldLabelClientError = Boolean(
        fieldSettings?.id &&
            fieldsError.some((fid) => String(fid) === String(fieldSettings.id)) &&
            !hasKickOffFieldLabel(fieldSettings.label)
    );
    const fieldServerMsg = fieldSettings?.serverErrorMessage;
    const fieldLabelErrorShow = fieldLabelClientError || Boolean(fieldServerMsg);

    const selectedSectionForSettings = selectedElement
        ? visibleSections.find((el) => el.id === selectedElement)
        : undefined;
    const sectionServerMsg = selectedSectionForSettings?.serverErrorMessage;
    const sectionTitleClientErr = Boolean(
        selectedSectionForSettings &&
            sectionsError.includes(selectedSectionForSettings.localId) &&
            !selectedSectionForSettings.title
    );
    const sectionTitleErrorShow = sectionTitleClientErr || Boolean(sectionServerMsg);

    return (
        <>
            <div className="builder">
                <div className="builder__header header">
                    <div className="header__block">
                        <div className="header__wrapper">
                            <div className="header__title">{data?.title}</div>
                            <div className="header__subtitle">Add and customize form for your needs</div>
                        </div>
                    </div>
                    <div className="header__block">
                        <div className="header__line"></div>
                        <div className="header__btn" onClick={saveAsDraftFunc}>
                            {isLoadingDraft ? <Loader color="#001965" /> : 'Save as Draft'}
                        </div>
                        <div className="header__btn" onClick={openPreview}>
                            {isOpeningPreview ? <Loader color="#001965" /> : 'Preview'}
                        </div>
                        <div className="header__btn primary" onClick={handlePublishClick}>
                            {isLoadingPublished ? <Loader color="white" /> : 'Publish'}
                        </div>
                    </div>
                </div>{' '}
                <DndContext
                    sensors={sensors}
                    collisionDetection={pointerWithin}
                    onDragStart={handleDragStart}
                    onDragCancel={handleDragCancel}
                    onDragEnd={handleDragEnd}
                >
                    <div className="builder__main main" style={{ display: 'flex', gap: 24 }}>
                        <img
                            src={BGImage}
                            alt="bg"
                            className="bg-image"
                            onClick={(e: any) => {
                                e.stopPropagation();
                                setSelectedElement(null);
                            }}
                        />

                        {/* Левая панель */}
                        <div className="main__basic-elements basic-elements">
                            <div className="basic-elements__title kik-off">
                                <div
                                    onClick={() => setSelectedTab('basic')}
                                    className={`tab ${selectedTab === 'basic' ? 'active' : ''}`}
                                >
                                    Basic Elements
                                </div>{' '}
                                <div
                                    onClick={() => setSelectedTab('linked')}
                                    className={`tab ${selectedTab === 'linked' ? 'active' : ''}`}
                                >
                                    Linked Elements
                                </div>
                            </div>
                            <div className="basic-elements__wrapper">
                                {selectedTab === 'basic' &&
                                    fields?.map((el: any) => (
                                        <DraggableItem id={el.id} key={el.id}>
                                            {iconsMap?.[el.type]('#001965')}
                                            {el.title}
                                        </DraggableItem>
                                    ))}
                                {selectedTab === 'linked' && (
                                    <LinkedFieldsComponent linkedFields={linkedFields} iconsMap={iconsMap} />
                                )}

                                <DragOverlay>
                                    {activeItem ? (
                                        activeItem.origin === 'palette' && !activeItem?.link_id ? (
                                            <div
                                                style={{
                                                    borderRadius: '12px',
                                                    background: 'var(--Backgrounds-Secoundary, #f5f7fa)',
                                                    width: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'var(--texts-icons-primary, #001965)',
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    padding: 12,
                                                    margin: '4px 0'
                                                }}
                                            >
                                                <>
                                                    {iconsMap[activeItem?.type]('#001965')}
                                                    <span style={{ marginLeft: 8, marginTop: '10px', fontWeight: 600 }}>
                                                        {activeItem.title}
                                                    </span>
                                                </>
                                            </div>
                                        ) : activeItem.origin === 'palette' && activeItem?.link_id ? (
                                            <div className={`linked-block `}>
                                                <div className="linked-block__fields" style={{ marginLeft: 24 }}>
                                                    <div className="linked-block__field">
                                                        <div className="linked-block__field-header">
                                                            {iconsMap?.[activeItem.type]?.('#808cb2')}{' '}
                                                            {activeItem.title}
                                                        </div>
                                                        <div className="linked-block__field-main">
                                                            {activeItem.label}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : activeItem.origin === 'form' ? (
                                            <SortableFormItemDragOverlay item={activeItem} />
                                        ) : null
                                    ) : null}
                                </DragOverlay>
                            </div>
                        </div>
                        {/* Центральная дроп-зона */}

                        <div className="main__form form" ref={formScrollRef}>
                            <div
                                className={`form__static-area static-area ${
                                    selectedElement === 'static' ? 'selected' : ''
                                }`}
                                onClick={() => setSelectedElement('static')}
                            >
                                <div className="static-area__top-row">
                                    <div className="static-area__icon">
                                        <SupplierQuestionaryFormIcon />
                                    </div>
                                </div>
                                <div className="static-area__title">{data?.title}</div>
                                <div className="static-area__description">
                                    {data?.description ? (
                                        <div
                                            className="static-area__description-color"
                                            style={{ whiteSpace: 'pre-wrap' }}
                                        >
                                            {data?.description}
                                        </div>
                                    ) : (
                                        'Form description (optional)'
                                    )}
                                </div>
                            </div>
                            {visibleSections.map((section, index) => (
                                <div
                                    className={`form__section section ${
                                        section?.id === selectedElement ? 'selected' : ''
                                    } ${
                                        sectionsError.some((id) => String(id) === String(section?.localId)) ||
                                        Boolean(section?.serverErrorMessage)
                                            ? 'error'
                                            : ''
                                    }`}
                                    data-builder-section={String(section.localId)}
                                    key={section?.id}
                                >
                                    {section?.id === selectedElement && visibleSections.length > 1 && (
                                        <div className="section__arrows">
                                            {index === visibleSections.length - 1 ? (
                                                <></>
                                            ) : (
                                                <>
                                                    {' '}
                                                    <Tooltip title="Move to bottom">
                                                        <div onClick={() => moveSectionToEnd(section?.localId)}>
                                                            <ArrowDownDown />
                                                        </div>
                                                    </Tooltip>
                                                    <Tooltip title="Move down">
                                                        <div onClick={() => moveSectionDown(section?.localId)}>
                                                            <ArrowDown />
                                                        </div>
                                                    </Tooltip>
                                                </>
                                            )}

                                            {index === 0 ? (
                                                <></>
                                            ) : (
                                                <>
                                                    <Tooltip title="Move up">
                                                        <div onClick={() => moveSectionUp(section?.localId)}>
                                                            <ArrowDown style={{ transform: 'rotate(180deg)' }} />
                                                        </div>
                                                    </Tooltip>
                                                    <Tooltip title="Move to top">
                                                        <div onClick={() => moveSectionToStart(section.localId)}>
                                                            <ArrowDownDown style={{ transform: 'rotate(180deg)' }} />
                                                        </div>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    <div className="section__header" onClick={() => setSelectedElement(section?.id)}>
                                        <div className="section__number">
                                            Section {index + 1} of {visibleSections.length}
                                        </div>
                                        <div
                                            className={`section__title ${
                                                (sectionsError.some((id) => String(id) === String(section?.localId)) &&
                                                    !section?.title) ||
                                                section?.serverErrorMessage
                                                    ? 'error'
                                                    : section?.title
                                                    ? ''
                                                    : 'noTitle'
                                            } `}
                                        >
                                            {section?.title ? section?.title : 'Section Name'}
                                        </div>
                                        <div className="section__description">
                                            {section?.description ? (
                                                <div
                                                    className="static-area__description-color"
                                                    style={{ whiteSpace: 'pre-wrap' }}
                                                >
                                                    {section?.description}
                                                </div>
                                            ) : (
                                                'Section description (optional)'
                                            )}
                                        </div>
                                    </div>
                                    <DroppableArea id={section.id} key={section.id} className="section__dropzone">
                                        {!!section?.fields?.length ? (
                                            <SortableContext
                                                items={section.fields.map((item) => item.id)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {section.fields.map((item) => (
                                                    <SortableFormItem
                                                        key={item.id}
                                                        id={item.id}
                                                        item={item}
                                                        selectedElement={selectedElement}
                                                        fieldsError={fieldsError}
                                                        onSelect={setSelectedElement}
                                                    />
                                                ))}
                                            </SortableContext>
                                        ) : (
                                            <div className="section__placeholder">
                                                Please drag & drop question from Basic Elements
                                            </div>
                                        )}
                                    </DroppableArea>
                                </div>
                            ))}

                            <div className="form__add-section" onClick={addNewSection}>
                                <AddSectionIcon /> Add Section
                            </div>
                        </div>
                        {/* Правая панель */}

                        <div className={`main__settings settings ${!selectedElement ? 'without' : ''}`}>
                            {selectedElement === 'static' && (
                                <>
                                    <div className="settings__title">Section Setting</div>
                                    <div className="settings__fields">
                                        <Form.Item>
                                            <FloatInput
                                                floatLabel={'Section Name'}
                                                className="input"
                                                value={data?.title}
                                                disabled
                                                maxLength={60}
                                            />
                                        </Form.Item>
                                        <Form.Item>
                                            <FloatTextArea
                                                floatLabel={'Section description (optional)'}
                                                className="input"
                                                hasError={() => false}
                                                value={data?.description}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                                ) => {
                                                    setData((prev) => ({
                                                        ...prev,
                                                        description: e.target.value
                                                    }));
                                                }}
                                            />
                                        </Form.Item>
                                    </div>
                                </>
                            )}
                            {visibleSections.find((elem) => elem.id === selectedElement) && (
                                <>
                                    <div className="settings__title">Section Setting</div>
                                    <div className="settings__fields">
                                        <Form.Item>
                                            <FloatInput
                                                floatLabel={'Section Name'}
                                                className="input"
                                                value={visibleSections.find((el) => el.id === selectedElement)?.title}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                                ) => {
                                                    setData((prev) => {
                                                        if (!prev) return prev;

                                                        const updatedSections = prev.sections.map((section) => {
                                                            if (section.id === selectedElement) {
                                                                return {
                                                                    ...section,
                                                                    title: e.target.value,
                                                                    serverErrorMessage: undefined
                                                                };
                                                            }
                                                            return section;
                                                        });

                                                        return {
                                                            ...prev,
                                                            sections: updatedSections
                                                        };
                                                    });
                                                }}
                                                maxLength={60}
                                                error={sectionTitleErrorShow}
                                            />
                                        </Form.Item>
                                        {sectionTitleErrorShow && (
                                            <div className="error-text" style={{ margin: '-6px 0 8px 0' }}>
                                                {sectionServerMsg || 'Section name is required'}
                                            </div>
                                        )}
                                        <Form.Item>
                                            <FloatTextArea
                                                floatLabel={'Section description'}
                                                className="input"
                                                hasError={() => false}
                                                value={
                                                    visibleSections.find((el) => el.id === selectedElement)?.description
                                                }
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                                ) => {
                                                    setData((prev) => {
                                                        if (!prev) return prev;

                                                        const updatedSections = prev.sections.map((section) => {
                                                            if (section.id === selectedElement) {
                                                                return {
                                                                    ...section,
                                                                    description: e.target.value,
                                                                    serverErrorMessage: undefined
                                                                };
                                                            }
                                                            return section;
                                                        });

                                                        return {
                                                            ...prev,
                                                            sections: updatedSections
                                                        };
                                                    });
                                                }}
                                            />
                                        </Form.Item>
                                        <>
                                            <Divider style={{ margin: '5px 0' }} />
                                            <div className="settings__row delete">
                                                <div
                                                    className="settings__delete"
                                                    onClick={() => setIsDeleteSection(true)}
                                                >
                                                    <Delete fill="#E80A0F" />
                                                </div>
                                            </div>
                                        </>
                                    </div>
                                </>
                            )}

                            {selectedElement &&
                                selectedElement !== 'static' &&
                                !data?.sections?.find((elem) => elem.id === selectedElement) && (
                                    <>
                                        {findItem()?.link_id && (
                                            <div className="settings__linked linked">
                                                <div className="linked__title">
                                                    <ClipIcon /> Linked from
                                                </div>
                                                <div className="linked__text">
                                                    {findItem()?.linked?.form} / {findItem()?.linked?.section} /{' '}
                                                    {findItem()?.linked?.component}
                                                </div>
                                            </div>
                                        )}
                                        <div className="settings__title">
                                            Field Setting
                                            <div className="settings__title-selected">
                                                {selectedElementData && iconsMap[selectedElementData?.type]('#808CB2')}
                                                {findItem()?.title}
                                            </div>
                                        </div>
                                        <div className="settings__fields">
                                            {findItem()?.type !== 'explanation-banner' ? (
                                                <>
                                                    <FloatInput
                                                        floatLabel={'Field label'}
                                                        className="input"
                                                        value={findItem()?.label}
                                                        onChange={(e) => updateFieldOrSection('label', e.target.value)}
                                                        maxLength={255}
                                                        error={fieldLabelErrorShow}
                                                    />
                                                    {fieldLabelErrorShow && (
                                                        <div className="error-text" style={{ margin: '-10px 0 0 0' }}>
                                                            {fieldServerMsg || 'This field is required'}
                                                        </div>
                                                    )}
                                                    <FloatTextArea
                                                        floatLabel={'Helper text'}
                                                        className="input"
                                                        hasError={() => false}
                                                        value={findItem()?.description}
                                                        onChange={(
                                                            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                                        ) => updateFieldOrSection('description', e.target.value)}
                                                        maxLength={512}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <FloatInput
                                                        floatLabel={'Field label'}
                                                        className="input"
                                                        value={findItem()?.label}
                                                        onChange={(e) => updateFieldOrSection('label', e.target.value)}
                                                        maxLength={255}
                                                        error={fieldLabelErrorShow}
                                                    />
                                                    {fieldLabelErrorShow && (
                                                        <div className="error-text" style={{ margin: '-10px 0 0 0' }}>
                                                            {fieldServerMsg || 'This field is required'}
                                                        </div>
                                                    )}
                                                    <FloatTextArea
                                                        floatLabel={'Helper text'}
                                                        className="input"
                                                        hasError={() => false}
                                                        value={findItem()?.description}
                                                        onChange={(
                                                            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                                        ) => updateFieldOrSection('description', e.target.value)}
                                                        maxLength={512}
                                                    />
                                                </>
                                            )}

                                            <div>
                                                <>
                                                    <Divider />
                                                    {/* options */}
                                                    {(() => {
                                                        const item = findItem();
                                                        if (!item || !item.options?.length) return null;

                                                        const isMultipleChoice = item?.type === 'multiple-choice';

                                                        return (
                                                            <>
                                                                <div>
                                                                    {item.options.map((elem, index) => {
                                                                        return (
                                                                            <>
                                                                                <div className="settings__option">
                                                                                    <div className="settings__option-input">
                                                                                        {isMultipleChoice ? (
                                                                                            <FloatTextArea
                                                                                                floatLabel={`Option ${
                                                                                                    index + 1
                                                                                                }`}
                                                                                                maxLength={512}
                                                                                                className="input"
                                                                                                value={elem?.value}
                                                                                                onChange={(e: any) =>
                                                                                                    updateOptionField(
                                                                                                        index,
                                                                                                        'value',
                                                                                                        e.target.value
                                                                                                    )
                                                                                                }
                                                                                                hasError={() =>
                                                                                                    findItem()?.id &&
                                                                                                    fieldsError.includes(
                                                                                                        findItem()!.id
                                                                                                    ) &&
                                                                                                    !elem?.value
                                                                                                        ? true
                                                                                                        : false
                                                                                                }
                                                                                                error={
                                                                                                    findItem()?.id &&
                                                                                                    fieldsError.includes(
                                                                                                        findItem()!.id
                                                                                                    ) &&
                                                                                                    !elem?.value
                                                                                                        ? true
                                                                                                        : false
                                                                                                }
                                                                                                disabled={elem.disabled}
                                                                                            />
                                                                                        ) : (
                                                                                            <FloatInput
                                                                                                floatLabel={`Option ${
                                                                                                    index + 1
                                                                                                }`}
                                                                                                maxLength={
                                                                                                    isMultipleChoice
                                                                                                        ? 512
                                                                                                        : 255
                                                                                                }
                                                                                                className="input"
                                                                                                value={elem?.value}
                                                                                                onChange={(e) =>
                                                                                                    updateOptionField(
                                                                                                        index,
                                                                                                        'value',
                                                                                                        e.target.value
                                                                                                    )
                                                                                                }
                                                                                                error={
                                                                                                    findItem()?.id &&
                                                                                                    fieldsError.includes(
                                                                                                        findItem()!.id
                                                                                                    ) &&
                                                                                                    !elem?.value
                                                                                                        ? true
                                                                                                        : false
                                                                                                }
                                                                                                disabled={elem.disabled}
                                                                                            />
                                                                                        )}

                                                                                        {findItem()?.id &&
                                                                                            fieldsError.includes(
                                                                                                findItem()!.id
                                                                                            ) &&
                                                                                            !elem?.value && (
                                                                                                <div
                                                                                                    className="error-text"
                                                                                                    style={{
                                                                                                        margin: '4px 0 0 0'
                                                                                                    }}
                                                                                                >
                                                                                                    This field is
                                                                                                    required
                                                                                                </div>
                                                                                            )}
                                                                                    </div>
                                                                                    {(findItem()?.settings?.find(
                                                                                        (elem) =>
                                                                                            elem.type ===
                                                                                            'rating-points'
                                                                                    )?.switchValue ||
                                                                                        !!(
                                                                                            findItem()?.link_id &&
                                                                                            elem.rating
                                                                                        )) && (
                                                                                        <div className="settings__option-rating-input">
                                                                                            <FloatInput
                                                                                                floatLabel={'Rating'}
                                                                                                className="input small-input"
                                                                                                value={
                                                                                                    elem?.rating ??
                                                                                                    undefined
                                                                                                }
                                                                                                disabled={true}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                    {item &&
                                                                                        item?.options &&
                                                                                        item?.options?.length > 1 &&
                                                                                        !item.link_id && (
                                                                                            <div
                                                                                                className="settings__option-delete"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    setIsDeleteOption(
                                                                                                        index + 1
                                                                                                    );
                                                                                                }}
                                                                                            >
                                                                                                <Delete fill="#808CB2" />
                                                                                            </div>
                                                                                        )}
                                                                                </div>
                                                                                {findItem()?.settings?.find(
                                                                                    (elem) =>
                                                                                        elem.type === 'go_to_section'
                                                                                )?.switchValue && (
                                                                                    <div className="settings__option-goto">
                                                                                        <Form.Item className="input-wrapper">
                                                                                            <FloatSelect
                                                                                                className="input"
                                                                                                floatLabel="Go to"
                                                                                                required={false}
                                                                                                err={false}
                                                                                                setSearch={() => {}}
                                                                                                options={visibleSections.map(
                                                                                                    (section) => ({
                                                                                                        value: section?.localId,
                                                                                                        name:
                                                                                                            section?.title ||
                                                                                                            'Section Name'
                                                                                                    })
                                                                                                )}
                                                                                                onChange={(
                                                                                                    value: any
                                                                                                ) => {
                                                                                                    updateOptionField(
                                                                                                        index,
                                                                                                        'go_to_next_section_local',
                                                                                                        value?.value
                                                                                                    );
                                                                                                    updateOptionField(
                                                                                                        index,
                                                                                                        'go_to_next_section',
                                                                                                        value?.value
                                                                                                    );
                                                                                                }}
                                                                                                search={''}
                                                                                                form={undefined}
                                                                                                hasError={() =>
                                                                                                    !!findItem()?.id &&
                                                                                                    fieldsError.includes(
                                                                                                        findItem()!.id
                                                                                                    ) &&
                                                                                                    !elem.go_to_next_section
                                                                                                }
                                                                                                type="common"
                                                                                                value={(() => {
                                                                                                    if (
                                                                                                        elem.go_to_next_section_local
                                                                                                    ) {
                                                                                                        const selectedSection =
                                                                                                            data?.sections.find(
                                                                                                                (
                                                                                                                    section
                                                                                                                ) =>
                                                                                                                    section.localId +
                                                                                                                        '' ===
                                                                                                                    elem.go_to_next_section_local +
                                                                                                                        ''
                                                                                                            );
                                                                                                        const selectedSectionTitle =
                                                                                                            data?.sections.find(
                                                                                                                (
                                                                                                                    section
                                                                                                                ) =>
                                                                                                                    section.localId +
                                                                                                                        '' ===
                                                                                                                    elem.go_to_next_section_local +
                                                                                                                        ''
                                                                                                            )?.title;

                                                                                                        return selectedSection
                                                                                                            ? {
                                                                                                                  label: selectedSectionTitle
                                                                                                                      ? selectedSectionTitle
                                                                                                                      : 'Section Name',
                                                                                                                  name: selectedSectionTitle
                                                                                                                      ? selectedSectionTitle
                                                                                                                      : 'Section Name'
                                                                                                              }
                                                                                                            : null;
                                                                                                    } else {
                                                                                                        const localIdSelectedSection =
                                                                                                            data?.sections.find(
                                                                                                                (
                                                                                                                    section
                                                                                                                ) =>
                                                                                                                    section.order ===
                                                                                                                    elem.go_to_next_section
                                                                                                            )?.localId;

                                                                                                        const selectedSectionTitle =
                                                                                                            data?.sections.find(
                                                                                                                (
                                                                                                                    section
                                                                                                                ) =>
                                                                                                                    section.localId ===
                                                                                                                    localIdSelectedSection
                                                                                                            )?.title;

                                                                                                        return localIdSelectedSection
                                                                                                            ? {
                                                                                                                  label: selectedSectionTitle
                                                                                                                      ? selectedSectionTitle
                                                                                                                      : 'Section Name',
                                                                                                                  name: selectedSectionTitle
                                                                                                                      ? selectedSectionTitle
                                                                                                                      : 'Section Name'
                                                                                                              }
                                                                                                            : null;
                                                                                                    }
                                                                                                })()}
                                                                                            />
                                                                                        </Form.Item>
                                                                                        {!!findItem()?.id &&
                                                                                            fieldsError.includes(
                                                                                                findItem()!.id
                                                                                            ) &&
                                                                                            !elem.go_to_next_section && (
                                                                                                <div className="error-text">
                                                                                                    This field is
                                                                                                    required
                                                                                                </div>
                                                                                            )}
                                                                                        <Divider />
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        );
                                                                    })}
                                                                </div>
                                                                <div className="settings__row">
                                                                    {findItem()?.link_id ? (
                                                                        <></>
                                                                    ) : (
                                                                        <div
                                                                            className="settings__add"
                                                                            onClick={() => addOption()}
                                                                        >
                                                                            <img src={Plus} />
                                                                            Add an option
                                                                        </div>
                                                                    )}
                                                                    {findItem()?.settings?.map((setting) => {
                                                                        if (setting?.type === 'rating-points') {
                                                                            return (
                                                                                <div className="settings__rating">
                                                                                    <div className="settings__rating-row">
                                                                                        Score{' '}
                                                                                        <Switch
                                                                                            checked={
                                                                                                setting?.switchValue
                                                                                            }
                                                                                            size="small"
                                                                                            className="custom-switch"
                                                                                            onChange={() =>
                                                                                                updateSettingField(
                                                                                                    'switchValue',
                                                                                                    !setting?.switchValue,
                                                                                                    setting?.type
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                    <div className="settings__rating-score">
                                                                                        Max. Score:{' '}
                                                                                        <span>
                                                                                            {maxSwitchValue} points
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        }
                                                                    })}
                                                                </div>
                                                                <Divider />
                                                            </>
                                                        );
                                                    })()}

                                                    {/* question category */}
                                                    {findItem()?.settings?.map((setting: SettingI) => {
                                                        if (
                                                            setting.type === 'question_category' &&
                                                            findItem()?.settings?.find(
                                                                (elem) => elem.type === 'rating-points'
                                                            )?.switchValue
                                                        ) {
                                                            return (
                                                                <>
                                                                    <div
                                                                        className={`settings__question_category ${
                                                                            findItem()?.id &&
                                                                            fieldsError.includes(findItem()!.id) &&
                                                                            !setting?.options.some(
                                                                                (option) => option.selected
                                                                            )
                                                                                ? 'error'
                                                                                : ''
                                                                        }`}
                                                                    >
                                                                        <div className="settings__question_category-title">
                                                                            Question Category
                                                                        </div>
                                                                        <div>
                                                                            {setting?.options?.map((elem, index) => {
                                                                                return (
                                                                                    <div className="settings__question_category-option">
                                                                                        <div
                                                                                            className={`settings__question_category-option-radio ${
                                                                                                elem.selected
                                                                                                    ? 'selected'
                                                                                                    : ''
                                                                                            }`}
                                                                                            onClick={() =>
                                                                                                updateRadioOptionSelectedInSetting(
                                                                                                    setting?.type,
                                                                                                    index
                                                                                                )
                                                                                            }
                                                                                        ></div>
                                                                                        {elem.label}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                    {findItem()?.id &&
                                                                        fieldsError.includes(findItem()!.id) &&
                                                                        !setting?.options.some(
                                                                            (option) => option.selected
                                                                        ) && (
                                                                            <div
                                                                                className="error-text"
                                                                                style={{
                                                                                    margin: '4px 0 0 0'
                                                                                }}
                                                                            >
                                                                                This field is required
                                                                            </div>
                                                                        )}
                                                                    <Divider />
                                                                </>
                                                            );
                                                        }
                                                    })}
                                                    {/* for linked */}
                                                    {findItem()?.linked_settings?.map((setting: SettingI) => {
                                                        if (
                                                            setting.type === 'question_category' &&
                                                            findItem()?.linked_settings?.find(
                                                                (elem) => elem.type === 'rating-points'
                                                            )?.switchValue
                                                        ) {
                                                            return (
                                                                <>
                                                                    <div
                                                                        className={`settings__question_category disable ${
                                                                            findItem()?.id &&
                                                                            fieldsError.includes(findItem()!.id) &&
                                                                            !setting?.options.some(
                                                                                (option) => option.selected
                                                                            )
                                                                                ? 'error'
                                                                                : ''
                                                                        }`}
                                                                    >
                                                                        <div className="settings__question_category-title">
                                                                            Question Category
                                                                        </div>
                                                                        <div>
                                                                            {setting?.options?.map((elem, index) => {
                                                                                return (
                                                                                    <div className="settings__question_category-option">
                                                                                        <div
                                                                                            className={`settings__question_category-option-radio ${
                                                                                                elem.selected
                                                                                                    ? 'selected'
                                                                                                    : ''
                                                                                            }`}
                                                                                        ></div>
                                                                                        {elem.label}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                    {findItem()?.id &&
                                                                        fieldsError.includes(findItem()!.id) &&
                                                                        !setting?.options.some(
                                                                            (option) => option.selected
                                                                        ) && (
                                                                            <div
                                                                                className="error-text"
                                                                                style={{
                                                                                    margin: '4px 0 0 0'
                                                                                }}
                                                                            >
                                                                                This field is required
                                                                            </div>
                                                                        )}
                                                                    <Divider />
                                                                </>
                                                            );
                                                        }
                                                    })}

                                                    {/* use in */}
                                                    {(() => {
                                                        const settings = findItem()?.settings ?? [];
                                                        const bidConditions = settings.find(
                                                            (s) => s?.type === 'bid_conditions'
                                                        );
                                                        const tenderBrief = settings.find(
                                                            (s) => s?.type === 'tender_brief'
                                                        );

                                                        if (!bidConditions && !tenderBrief) return null;

                                                        return (
                                                            <div
                                                                className={`settings__question_category ${
                                                                    findItem()?.id &&
                                                                    fieldsError.includes(findItem()!.id) &&
                                                                    tenderBrief?.switchValue &&
                                                                    !tenderBrief?.options?.some(
                                                                        (option) => option.selected
                                                                    )
                                                                        ? 'error'
                                                                        : ''
                                                                }`}
                                                            >
                                                                <div className="settings__question_category-title">
                                                                    Use in
                                                                </div>

                                                                {bidConditions && (
                                                                    <>
                                                                        <div className="settings__use-in">
                                                                            {bidConditions?.title}
                                                                            <Switch
                                                                                checked={bidConditions?.switchValue}
                                                                                size="small"
                                                                                className="custom-switch"
                                                                                onChange={() =>
                                                                                    updateSettingField(
                                                                                        'switchValue',
                                                                                        !bidConditions?.switchValue,
                                                                                        'bid_conditions'
                                                                                    )
                                                                                }
                                                                            />
                                                                        </div>

                                                                        {bidConditions?.switchValue && (
                                                                            <div className="settings__option-goto">
                                                                                <Form.Item className="input-wrapper">
                                                                                    <FloatSelect
                                                                                        className="input"
                                                                                        floatLabel="Select Section"
                                                                                        required={false}
                                                                                        err={false}
                                                                                        setSearch={() => {}}
                                                                                        options={bidConditions?.options?.map(
                                                                                            (option) => ({
                                                                                                value: option?.value,
                                                                                                name: option?.label
                                                                                            })
                                                                                        )}
                                                                                        onChange={(value: any) => {
                                                                                            if (value) {
                                                                                                updateOptionSelectedInSetting(
                                                                                                    bidConditions?.type,
                                                                                                    value?.value
                                                                                                );
                                                                                            } else {
                                                                                                deselectAllOptions(
                                                                                                    bidConditions?.type
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        search={''}
                                                                                        form={undefined}
                                                                                        hasError={() => false}
                                                                                        type="common"
                                                                                        value={
                                                                                            bidConditions?.options?.some(
                                                                                                (option) =>
                                                                                                    option.selected
                                                                                            )
                                                                                                ? {
                                                                                                      value: bidConditions?.options?.find(
                                                                                                          (option) =>
                                                                                                              option?.selected
                                                                                                      )?.label,
                                                                                                      name: bidConditions?.options?.find(
                                                                                                          (option) =>
                                                                                                              option?.selected
                                                                                                      )?.label
                                                                                                  }
                                                                                                : null
                                                                                        }
                                                                                    />
                                                                                </Form.Item>
                                                                                {findItem()?.id &&
                                                                                    fieldsError.includes(
                                                                                        findItem()!.id
                                                                                    ) &&
                                                                                    bidConditions?.switchValue &&
                                                                                    !bidConditions?.options?.some(
                                                                                        (option) => option.selected
                                                                                    ) && (
                                                                                        <div
                                                                                            className="error-text"
                                                                                            style={{
                                                                                                margin: '0 0 0 0'
                                                                                            }}
                                                                                        >
                                                                                            This field is required
                                                                                        </div>
                                                                                    )}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}

                                                                {tenderBrief && (
                                                                    <>
                                                                        <div className="settings__use-in">
                                                                            {tenderBrief?.title}
                                                                            <Switch
                                                                                checked={tenderBrief?.switchValue}
                                                                                size="small"
                                                                                className="custom-switch"
                                                                                onChange={() =>
                                                                                    updateSettingField(
                                                                                        'switchValue',
                                                                                        !tenderBrief?.switchValue,
                                                                                        'tender_brief'
                                                                                    )
                                                                                }
                                                                            />
                                                                        </div>

                                                                        {tenderBrief?.switchValue && (
                                                                            <div className="settings__option-goto">
                                                                                <Form.Item className="input-wrapper">
                                                                                    <FloatSelect
                                                                                        className="input"
                                                                                        floatLabel="Select Section"
                                                                                        required={false}
                                                                                        err={false}
                                                                                        setSearch={() => {}}
                                                                                        options={tenderBrief?.options?.map(
                                                                                            (option) => ({
                                                                                                value: option?.value,
                                                                                                name: option?.label
                                                                                            })
                                                                                        )}
                                                                                        onChange={(value: any) => {
                                                                                            if (value) {
                                                                                                updateOptionSelectedInSetting(
                                                                                                    tenderBrief?.type,
                                                                                                    value?.value
                                                                                                );
                                                                                            } else {
                                                                                                deselectAllOptions(
                                                                                                    tenderBrief?.type
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        search={''}
                                                                                        form={undefined}
                                                                                        hasError={() => false}
                                                                                        type="common"
                                                                                        value={
                                                                                            tenderBrief?.options?.some(
                                                                                                (option) =>
                                                                                                    option.selected
                                                                                            )
                                                                                                ? {
                                                                                                      value: tenderBrief?.options?.find(
                                                                                                          (option) =>
                                                                                                              option?.selected
                                                                                                      )?.label,
                                                                                                      name: tenderBrief?.options?.find(
                                                                                                          (option) =>
                                                                                                              option?.selected
                                                                                                      )?.label
                                                                                                  }
                                                                                                : null
                                                                                        }
                                                                                    />
                                                                                </Form.Item>
                                                                                {findItem()?.id &&
                                                                                    fieldsError.includes(
                                                                                        findItem()!.id
                                                                                    ) &&
                                                                                    tenderBrief?.switchValue &&
                                                                                    !tenderBrief?.options?.some(
                                                                                        (option) => option.selected
                                                                                    ) && (
                                                                                        <div
                                                                                            className="error-text"
                                                                                            style={{
                                                                                                margin: '0 0 0 0'
                                                                                            }}
                                                                                        >
                                                                                            This field is required
                                                                                        </div>
                                                                                    )}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}

                                                    {/* response validation */}
                                                    <ResponceValidation
                                                        findItem={findItem}
                                                        fieldsError={fieldsError}
                                                        updateSettingField={updateSettingField}
                                                        updateOptionSelectedInSetting={updateOptionSelectedInSetting}
                                                        deselectAllOptions={deselectAllOptions}
                                                        updateNestedOptionSelected={updateNestedOptionSelected}
                                                        deselectAllNestedOptions={deselectAllNestedOptions}
                                                        updateNestedOptionField={updateNestedOptionField}
                                                        updateRadioOptionSelectedInSetting={
                                                            updateRadioOptionSelectedInSetting
                                                        }
                                                    />

                                                    {/* Per Service Component */}
                                                    {findItem()?.settings?.map((setting) => {
                                                        if (setting.type === 'per_service_component') {
                                                            const item = findItem();
                                                            const linkedPerService = item?.linked_settings?.find(
                                                                (s) => s.type === 'per_service_component'
                                                            );
                                                            const displaySetting =
                                                                item?.link_id && linkedPerService
                                                                    ? linkedPerService
                                                                    : setting;

                                                            return (
                                                                <>
                                                                    <div className="settings__per-component">
                                                                        Per Service Component{' '}
                                                                        <Switch
                                                                            checked={displaySetting?.switchValue}
                                                                            size="small"
                                                                            className="custom-switch"
                                                                            onChange={() =>
                                                                                updateSettingField(
                                                                                    'switchValue',
                                                                                    !setting?.switchValue,
                                                                                    setting?.type
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                findItem()?.link_id ? true : false
                                                                            }
                                                                        />
                                                                    </div>

                                                                    {displaySetting?.switchValue && (
                                                                        <div className="settings__related-components ">
                                                                            <Form>
                                                                                <Form.Item className="input-wrapper-multiselect">
                                                                                    <CustomMultiSelect
                                                                                        disabled={
                                                                                            findItem()?.link_id
                                                                                                ? true
                                                                                                : false
                                                                                        }
                                                                                        required={false}
                                                                                        floatLabel={
                                                                                            'Select related components'
                                                                                        }
                                                                                        err={false}
                                                                                        options={displaySetting?.options
                                                                                            ?.filter(
                                                                                                (elem) => !elem.selected
                                                                                            )
                                                                                            .map((elem) => ({
                                                                                                name: elem.label,
                                                                                                id: elem.value
                                                                                            }))}
                                                                                        onChange={(
                                                                                            selectedValues: any
                                                                                        ) => {
                                                                                            if (
                                                                                                selectedValues?.name ===
                                                                                                'selectedAll'
                                                                                            ) {
                                                                                                selectAllOptions(
                                                                                                    setting?.type
                                                                                                );
                                                                                            } else {
                                                                                                updateOptionMultiSelectedInSetting(
                                                                                                    setting?.type,
                                                                                                    selectedValues?.id
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        hasError={() => false}
                                                                                        prefix={undefined}
                                                                                        value={
                                                                                            displaySetting?.options
                                                                                                ?.filter(
                                                                                                    (option) =>
                                                                                                        option.selected
                                                                                                )
                                                                                                .map((option) => ({
                                                                                                    name: option.label,
                                                                                                    id: option.value
                                                                                                })) || []
                                                                                        }
                                                                                        deleteItem={(
                                                                                            selectedValues: any
                                                                                        ) => {
                                                                                            updateOptionMultiSelectedInSetting(
                                                                                                setting?.type,
                                                                                                selectedValues
                                                                                            );
                                                                                        }}
                                                                                        deleteAllItems={() =>
                                                                                            deselectAllOptions(
                                                                                                setting?.type
                                                                                            )
                                                                                        }
                                                                                        setTouched={() => {}}
                                                                                    />
                                                                                </Form.Item>
                                                                            </Form>
                                                                        </div>
                                                                    )}

                                                                    <Divider />
                                                                </>
                                                            );
                                                        }
                                                        if (setting.type === 'critical') {
                                                            return (
                                                                <>
                                                                    <div className="settings__per-component">
                                                                        <span>
                                                                            Needs Review{' '}
                                                                            <Tooltip title="The questions must be double-confirmed by the TE during the define phase.">
                                                                                <img src={i} />
                                                                            </Tooltip>
                                                                        </span>
                                                                        <Switch
                                                                            checked={setting?.switchValue}
                                                                            size="small"
                                                                            className="custom-switch"
                                                                            onChange={() =>
                                                                                updateSettingField(
                                                                                    'switchValue',
                                                                                    !setting?.switchValue,
                                                                                    setting?.type
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>

                                                                    <Divider />
                                                                </>
                                                            );
                                                        }
                                                        if (setting.type === 'file_upload_formats') {
                                                            return (
                                                                <div
                                                                    className="settings__question_category"
                                                                    key={setting.type}
                                                                >
                                                                    <div className="settings__question_category-title">
                                                                        Format
                                                                    </div>

                                                                    <div>
                                                                        {setting?.options?.map((elem) => {
                                                                            return (
                                                                                <div
                                                                                    key={elem.value}
                                                                                    className="settings__checkbox"
                                                                                >
                                                                                    <Checkbox
                                                                                        checked={elem.selected}
                                                                                        onChange={(value) => {
                                                                                            updateOptionMultiSelectedInSetting(
                                                                                                setting?.type,
                                                                                                elem.value
                                                                                            );
                                                                                        }}
                                                                                    />
                                                                                    {elem.label}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                    })}

                                                    <div className="settings__row">
                                                        {/* required */}
                                                        <div className="settings__row-wrapper">
                                                            {findItem()?.settings?.map((setting) => {
                                                                if (setting?.type === 'required') {
                                                                    return (
                                                                        <div className="settings__required">
                                                                            Required
                                                                            <Switch
                                                                                checked={setting?.switchValue}
                                                                                size="small"
                                                                                className="custom-switch"
                                                                                onChange={() =>
                                                                                    updateSettingField(
                                                                                        'switchValue',
                                                                                        !setting?.switchValue,
                                                                                        setting?.type
                                                                                    )
                                                                                }
                                                                            />
                                                                        </div>
                                                                    );
                                                                }
                                                            })}
                                                        </div>
                                                        <div className="settings__row-wrapper">
                                                            {findItem()?.settings?.map((setting) => {
                                                                if (setting.type === 'go_to_section') {
                                                                    return (
                                                                        <Tooltip
                                                                            title={
                                                                                setting?.switchValue
                                                                                    ? ''
                                                                                    : 'Go to section based on answer'
                                                                            }
                                                                        >
                                                                            <div
                                                                                className={`settings__hide icons ${
                                                                                    setting?.switchValue
                                                                                        ? 'selected'
                                                                                        : ''
                                                                                }`}
                                                                                onClick={() =>
                                                                                    updateSettingField(
                                                                                        'switchValue',
                                                                                        !setting?.switchValue,
                                                                                        setting?.type
                                                                                    )
                                                                                }
                                                                            >
                                                                                <GoToSectionIcon
                                                                                    fill={
                                                                                        setting?.switchValue
                                                                                            ? 'white'
                                                                                            : '#001965'
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </Tooltip>
                                                                    );
                                                                }
                                                            })}
                                                            <div
                                                                className="settings__delete icons"
                                                                onClick={() => setIsDeleteItem(true)}
                                                            >
                                                                <Delete fill="#E80A0F" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            </div>
                                        </div>
                                    </>
                                )}
                        </div>
                    </div>
                </DndContext>
            </div>
            <Modal
                className={''}
                centered
                open={!!isDeleteOption}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIsDeleteOption(null)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={(e) => {
                                if (!isDeleteOption) return;
                                e.stopPropagation();
                                removeOption(isDeleteOption - 1);
                                setIsDeleteOption(null);
                            }}
                        >
                            Delete
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">
                    <div className="modal-confirm__title" style={{ margin: '0 0 10px 0' }}>
                        Delete option?
                    </div>
                    Are you sure you want to delete the
                    <b>
                        {isDeleteOption && findItem()?.options?.find((_, index) => index === isDeleteOption - 1)?.label}
                    </b>{' '}
                    from basic element - {findItem()?.title}?
                </div>
            </Modal>
            <Modal
                className={''}
                centered
                open={!!isDeleteItem}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIsDeleteItem(false)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={(e) => {
                                if (!isDeleteItem) return;
                                e.stopPropagation();
                                removeItem();
                                setIsDeleteItem(false);
                                setSelectedElement(null);
                            }}
                        >
                            Delete
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">
                    <div className="modal-confirm__title" style={{ margin: '0 0 10px 0' }}>
                        Delete basic element?
                    </div>
                    Are you sure you want to delete the <b>{findItem()?.title}</b> basic element?
                </div>
            </Modal>
            <Modal
                className={''}
                centered
                open={!!isDeleteSection}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIsDeleteSection(false)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={(e) => {
                                if (!isDeleteSection) return;
                                e.stopPropagation();
                                removeSection();
                                setIsDeleteSection(false);
                                setSelectedElement(null);
                            }}
                        >
                            Delete
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">
                    <div className="modal-confirm__title" style={{ margin: '0 0 10px 0' }}>
                        Delete section?
                    </div>
                    Are you sure you want to delete the{' '}
                    <b>{data?.sections?.find((elem) => elem.id === selectedElement)?.title ?? 'Section Name'}</b>{' '}
                    section?
                </div>
            </Modal>
            <Modal
                className={''}
                centered
                open={isCantPublishModal}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ width: '100%', margin: '30px 0 0 0' }}>
                        <Button
                            className={``}
                            style={{ width: '100%' }}
                            type="primary"
                            onClick={() => {
                                setIsCantPublishModal(false);
                            }}
                        >
                            Got it
                        </Button>
                    </div>
                }
            >
                <div className="modal-confirm">
                    <div className="modal-confirm__title" style={{ margin: '0 0 10px 0' }}>
                        Can’t publish document
                    </div>
                    Add a custom section to publish. The document must include at least one custom section with fields.
                </div>
            </Modal>
            {previewModal}
        </>
    );
};
